/**
 * Core Game Engine: "Bertahan Dari Anomali"
 * 
 * Coordinates:
 * - 60 FPS target loop with fixed timestep integration
 * - Hitstop microfreeze & slow-motion knockout
 * - Combat hitbox vs hurtbox resolution
 * - Particle pool and VFX rendering
 * - Objective tracking and wave victory
 * - F2 Debug hitbox visualization
 */

import { Player } from "../entities/Player";
import { Enemy } from "../entities/Enemy";
import { GameMap } from "../world/GameMap";
import { Camera } from "../world/Camera";
import { TelegraphSystem } from "../combat/TelegraphSystem";
import { TargetLockSystem } from "../combat/TargetLockSystem";
import { DamageSystem } from "../combat/DamageSystem";
import { testHitboxOverlap, renderDebugHitboxes, Hurtbox } from "../combat/HitboxSystem";
import { anomaliAudio } from "../audio/AnomaliAudio";
import { SaveSystem, GameSaveData } from "../save/SaveSystem";
import { assetManager } from "./AssetManager";

export interface CombatParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
}

export class GameEngine {
  public player: Player;
  public enemies: Enemy[] = [];
  public map: GameMap;
  public camera: Camera;
  public telegraphSystem: TelegraphSystem;
  public targetLockSystem: TargetLockSystem;
  public damageSystem: DamageSystem;

  public particles: CombatParticle[] = [];
  public score: number = 0;
  public defeatedCount: number = 0;
  public targetTotalCount: number = 6;

  public hitstopTimer: number = 0; // Microfreeze seconds
  public slowmoTimer: number = 0;   // Slow-mo seconds
  public debugHitboxes: boolean = false;

  public isPaused: boolean = false;
  public isVictory: boolean = false;
  public isGameOver: boolean = false;

  public saveData: GameSaveData;

  constructor() {
    this.saveData = SaveSystem.load();
    this.map = new GameMap();
    this.camera = new Camera(420, 500);
    this.player = new Player(420, 500);
    this.telegraphSystem = new TelegraphSystem();
    this.targetLockSystem = new TargetLockSystem();
    this.damageSystem = new DamageSystem();
    assetManager.preloadAll();

    this.spawnMissionEnemies();
  }

  /**
   * Spawn 6 prototype anomalies across the ruins as specified in game design
   */
  public spawnMissionEnemies() {
    this.enemies = [
      new Enemy("enemy_m1", "melee", 620, 360),
      new Enemy("enemy_m2", "melee", 780, 520),
      new Enemy("enemy_s1", "fast", 540, 680),
      new Enemy("enemy_s2", "fast", 920, 340),
      new Enemy("enemy_r1", "ranged", 1060, 480),
      new Enemy("enemy_h1", "heavy", 840, 420), // Central Horned Shield Brute
    ];
    this.targetTotalCount = this.enemies.length;
    this.defeatedCount = 0;
  }

  public restart() {
    this.map = new GameMap();
    this.player = new Player(420, 500);
    this.camera = new Camera(420, 500);
    this.telegraphSystem.clearAll();
    this.targetLockSystem.clearLock();
    this.damageSystem.clear();
    this.particles = [];
    this.score = 0;
    this.isVictory = false;
    this.isGameOver = false;
    this.hitstopTimer = 0;
    this.slowmoTimer = 0;

    this.spawnMissionEnemies();
    anomaliAudio.startBgm();
  }

  /**
   * Spawn spark particles on impact
   */
  public spawnHitSparks(x: number, y: number, color: string = "#ffdd44", count: number = 8) {
    if (this.particles.length > 120) {
      this.particles.splice(0, this.particles.length - 120);
    }
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 120;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0,
        maxLife: 0.15 + Math.random() * 0.2,
        color,
        size: 1.5 + Math.random() * 2,
      });
    }
  }

  /**
   * Main Engine Step (called ~60 FPS)
   */
  public update(dt: number) {
    if (this.isPaused) return;

    // Hitstop microfreeze (freezes animation and physical combat movement)
    if (this.hitstopTimer > 0) {
      this.hitstopTimer -= dt;
      return;
    }

    // Slow-motion multiplier
    let effectiveDt = dt;
    if (this.slowmoTimer > 0) {
      this.slowmoTimer -= dt;
      effectiveDt *= 0.3;
    }

    const dtMs = effectiveDt * 1000;

    // 1. Update Player
    this.player.update(effectiveDt, this.map);
    if (this.player.isDead() && !this.isGameOver) {
      this.isGameOver = true;
    }

    // 2. Update Target Lock
    this.targetLockSystem.update(this.player.x, this.player.y);

    // 3. Update Telegraph System
    this.telegraphSystem.update(dtMs);

    // 4. Update Enemies
    for (const enemy of this.enemies) {
      enemy.update(
        effectiveDt,
        this.player.x,
        this.player.y,
        this.player.isDead(),
        this.map,
        this.telegraphSystem
      );
    }

    // 5. Combat Hitbox & Collision Resolution
    this.resolveCombatCollisions();

    // 6. Update Damage Numbers & Particles
    this.damageSystem.update(effectiveDt);

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += effectiveDt;
      p.x += p.vx * effectiveDt;
      p.y += p.vy * effectiveDt;
      p.vx *= 0.92;
      p.vy *= 0.92;
      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
      }
    }

    // 7. Update Camera
    this.camera.setTarget(this.player.x, this.player.y);
    this.camera.update(effectiveDt);

    // 8. Check Victory Condition
    if (!this.isVictory && !this.isGameOver) {
      const aliveCount = this.enemies.filter((e) => !e.isDead).length;
      this.defeatedCount = this.targetTotalCount - aliveCount;

      if (aliveCount === 0) {
        this.isVictory = true;
        this.slowmoTimer = 1.8;
        this.camera.addShake(10);
        anomaliAudio.playSfx("victory");

        // Update save stats
        this.saveData.clearedRuns++;
        this.saveData.enemiesDefeatedTotal += this.targetTotalCount;
        this.saveData.highScore = Math.max(this.saveData.highScore, this.score);
        SaveSystem.save(this.saveData);
      }
    }
  }

  /**
   * Resolve all Hitbox vs Hurtbox Collisions
   */
  private resolveCombatCollisions() {
    // A. Player Melee Hitboxes vs Enemies
    for (const hb of this.player.currentHitboxes) {
      for (const enemy of this.enemies) {
        if (enemy.isDead || hb.hitEntities.has(enemy.id)) continue;

        const hurtbox: Hurtbox = {
          entityId: enemy.id,
          isPlayer: false,
          x: enemy.x,
          y: enemy.y,
          radius: 18,
        };

        if (testHitboxOverlap(hb.shape, hurtbox)) {
          hb.hitEntities.add(enemy.id);

          // Calculate Damage
          const { damage, isCrit } = DamageSystem.calculateDamage(
            hb.damage,
            enemy.stats.defense,
            1.0,
            0.18,
            1.6
          );

          // Calculate Knockback Vector
          const kbAngle = Math.atan2(enemy.y - this.player.y, enemy.x - this.player.x);
          const kbVx = Math.cos(kbAngle) * hb.knockback * 8;
          const kbVy = Math.sin(kbAngle) * hb.knockback * 8;

          enemy.takeDamage(damage, kbVx, kbVy, this.telegraphSystem);

          // Spawn floating numbers & hit sparks
          this.damageSystem.spawnFloatingNumber(enemy.x, enemy.y, `${damage}`, "#ffffff", isCrit);
          this.spawnHitSparks(enemy.x, enemy.y, isCrit ? "#ffea55" : "#55e0ff", isCrit ? 12 : 7);

          // Camera shake on heavy hit / special
          if (hb.hitstopMs >= 100) {
            this.camera.addShake(8);
          }
          this.hitstopTimer = hb.hitstopMs / 1000;

          if (enemy.isDead) {
            this.score += enemy.stats.scoreValue;
          }
        }
      }
    }

    // B. Player Crescent Projectiles vs Enemies
    for (const p of this.player.projectiles) {
      if (!p.active) continue;

      for (const enemy of this.enemies) {
        if (enemy.isDead || p.hitEntities.has(enemy.id)) continue;

        const dist = Math.hypot(enemy.x - p.x, enemy.y - p.y);
        if (dist <= 26) {
          p.hitEntities.add(enemy.id);

          const { damage, isCrit } = DamageSystem.calculateDamage(
            p.damage,
            enemy.stats.defense,
            1.0,
            0.15,
            1.5
          );

          const kbAngle = p.angle;
          const kbVx = Math.cos(kbAngle) * p.knockback * 6;
          const kbVy = Math.sin(kbAngle) * p.knockback * 6;

          enemy.takeDamage(damage, kbVx, kbVy, this.telegraphSystem);
          this.damageSystem.spawnFloatingNumber(enemy.x, enemy.y, `${damage}`, "#66ddff", isCrit);
          this.spawnHitSparks(enemy.x, enemy.y, "#66eeff", 10);

          if (enemy.isDead) {
            this.score += enemy.stats.scoreValue;
          }
        }
      }
    }

    // C. Enemy Melee Hitboxes vs Player
    if (!this.player.isDead() && !this.player.isInvulnerable()) {
      const playerHurtbox: Hurtbox = {
        entityId: this.player.id,
        isPlayer: true,
        x: this.player.x,
        y: this.player.y,
        radius: 14,
      };

      for (const enemy of this.enemies) {
        if (enemy.isDead) continue;

        // Melee hitboxes
        for (const hb of enemy.activeHitboxes) {
          if (hb.hitEntities.has(this.player.id)) continue;

          if (testHitboxOverlap(hb.shape, playerHurtbox)) {
            hb.hitEntities.add(this.player.id);

            const { damage } = DamageSystem.calculateDamage(
              hb.damage,
              14, // player defense
              1.0,
              0.0
            );

            const kbAngle = Math.atan2(this.player.y - enemy.y, this.player.x - enemy.x);
            const kbVx = Math.cos(kbAngle) * hb.knockback * 6;
            const kbVy = Math.sin(kbAngle) * hb.knockback * 6;

            this.player.takeDamage(damage, kbVx, kbVy);
            this.damageSystem.spawnFloatingNumber(this.player.x, this.player.y, `-${damage}`, "#ff4444");
            this.spawnHitSparks(this.player.x, this.player.y, "#ff3333", 8);
            this.camera.addShake(6);
            this.hitstopTimer = 0.08;
          }
        }

        // Arrow projectiles vs Player
        for (let i = enemy.arrows.length - 1; i >= 0; i--) {
          const arrow = enemy.arrows[i];
          const dist = Math.hypot(this.player.x - arrow.x, this.player.y - arrow.y);
          if (dist <= 18) {
            const { damage } = DamageSystem.calculateDamage(arrow.damage, 14, 1.0, 0.0);
            const kbVx = Math.cos(arrow.angle) * arrow.knockback * 5;
            const kbVy = Math.sin(arrow.angle) * arrow.knockback * 5;

            this.player.takeDamage(damage, kbVx, kbVy);
            this.damageSystem.spawnFloatingNumber(this.player.x, this.player.y, `-${damage}`, "#ff5555");
            this.spawnHitSparks(this.player.x, this.player.y, "#ff7744", 6);
            this.camera.addShake(4);

            enemy.arrows.splice(i, 1);
          }
        }
      }
    }
  }

  /**
   * Render Full World Frame
   */
  public render(ctx: CanvasRenderingContext2D, now: number) {
    // 1. Camera transform
    this.camera.applyTransform(ctx);

    // 2. Render World Map Terrain
    this.map.render(ctx, this.camera.x, this.camera.y, this.camera.viewportWidth, this.camera.viewportHeight, now);

    // 3. Render Enemy Attack Telegraphs (RED ZONES on the ground BEFORE damage happens)
    this.telegraphSystem.render(ctx);

    // 4. Render Torch Lighting Layer
    this.map.renderTorchLighting(ctx, now);

    // 5. Render Entities sorted by Y coordinate for natural 2.5D depth
    const renderables: { y: number; draw: () => void }[] = [];

    // Player
    renderables.push({
      y: this.player.y,
      draw: () => this.player.render(ctx, now),
    });

    // Enemies
    for (const enemy of this.enemies) {
      if (!enemy.isDead) {
        renderables.push({
          y: enemy.y,
          draw: () => enemy.render(ctx, now),
        });
      }
    }

    renderables.sort((a, b) => a.y - b.y);
    for (const item of renderables) {
      item.draw();
    }

    // 6. Render Target Lock Indicator (Small diamond directly above targeted enemy)
    this.targetLockSystem.render(ctx, now);

    // 7. Render Particles with Additive Blending (Pixi-style)
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const p of this.particles) {
      const lifeRatio = p.life / p.maxLife;
      const alpha = Math.max(0, 1 - lifeRatio);
      const currentSize = Math.max(0.5, p.size * (1 - lifeRatio * 0.7));
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, currentSize, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // 8. Render Floating Damage Numbers
    this.damageSystem.render(ctx);

    // 9. Debug Hitboxes (F2 Toggle)
    if (this.debugHitboxes) {
      const allHitboxes = [...this.player.currentHitboxes];
      for (const e of this.enemies) {
        allHitboxes.push(...e.activeHitboxes);
      }
      const hurtboxes: Hurtbox[] = [
        {
          entityId: this.player.id,
          isPlayer: true,
          x: this.player.x,
          y: this.player.y,
          radius: 14,
        },
      ];
      for (const e of this.enemies) {
        if (!e.isDead) {
          hurtboxes.push({
            entityId: e.id,
            isPlayer: false,
            x: e.x,
            y: e.y,
            radius: 18,
          });
        }
      }
      renderDebugHitboxes(ctx, allHitboxes, hurtboxes);
    }

    // Restore Camera transform
    this.camera.restore(ctx);

    // 10. Atmospheric Ruins Vignette (Pixi/Canvas visual polish)
    const vw = this.camera.viewportWidth;
    const vh = this.camera.viewportHeight;
    if (vw > 0 && vh > 0) {
      const vignette = ctx.createRadialGradient(
        vw / 2,
        vh / 2,
        Math.min(vw, vh) * 0.35,
        vw / 2,
        vh / 2,
        Math.hypot(vw / 2, vh / 2)
      );
      vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
      vignette.addColorStop(1, "rgba(4, 8, 12, 0.42)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, vw, vh);

      // 11. Ambient Floating Anomaly Embers
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < 24; i++) {
        const px = ((now * 0.02 * (1 + (i % 3) * 0.5) + i * 97) % (vw + 40)) - 20;
        const py = ((now * -0.015 * (1 + (i % 2) * 0.4) + i * 73 + Math.sin(now * 0.002 + i) * 20) % (vh + 40)) - 20;
        const actualPy = py < -20 ? py + vh + 40 : py;
        const size = 1.2 + (i % 3) * 0.8;
        const alpha = 0.25 + Math.sin(now * 0.004 + i) * 0.15;
        ctx.fillStyle = i % 2 === 0 ? `rgba(0, 240, 255, ${alpha})` : `rgba(255, 180, 50, ${alpha})`;
        ctx.beginPath();
        ctx.arc(px, actualPy, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }
}
